import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Row-tint palette. `bg` is applied to the <tr>; `swatch` is the picker
// button background. Keeping this UI-owned (vs a backend enum) means we
// can add colors without a migration — the server just persists the key.
const LEAD_ROW_COLORS = [
    { key: "", label: "Clear", bg: "", swatch: "bg-transparent border border-slate-500" },
    { key: "amber", label: "Hot", bg: "bg-amber-500/25 hover:bg-amber-500/30", swatch: "bg-amber-500" },
    { key: "emerald", label: "Won", bg: "bg-emerald-500/25 hover:bg-emerald-500/30", swatch: "bg-emerald-500" },
    { key: "sky", label: "Following up", bg: "bg-sky-500/25 hover:bg-sky-500/30", swatch: "bg-sky-500" },
    { key: "violet", label: "On hold", bg: "bg-violet-500/25 hover:bg-violet-500/30", swatch: "bg-violet-500" },
    { key: "rose", label: "Lost", bg: "bg-rose-500/25 hover:bg-rose-500/30", swatch: "bg-rose-500" },
    { key: "slate", label: "Cold", bg: "bg-slate-500/30 hover:bg-slate-500/40", swatch: "bg-slate-500" },
];
const _COLOR_BY_KEY = Object.fromEntries(LEAD_ROW_COLORS.map((c) => [c.key, c]));
function rowBgClass(key) {
    return _COLOR_BY_KEY[key || ""]?.bg || "";
}

// iter-166 — Live cursor chips. Renders overlapping avatar bubbles
// with a teammate's initials next to each row where they're currently
// typing. Uses a deterministic 8-color palette so the same user always
// gets the same chip color across sessions.
const _CHIP_COLORS = [
    "bg-amber-500", "bg-emerald-500", "bg-sky-500", "bg-violet-500",
    "bg-rose-500", "bg-cyan-500", "bg-fuchsia-500", "bg-lime-500",
];
function _chipColor(seed) {
    let h = 0;
    for (let i = 0; i < (seed || "").length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
    return _CHIP_COLORS[Math.abs(h) % _CHIP_COLORS.length];
}
function _initials(name) {
    const parts = (name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}
function TypingChips({ users = [], size = "sm", testid }) {
    if (!users.length) return null;
    const shown = users.slice(0, 3);
    const extra = users.length - shown.length;
    const dim = size === "sm" ? "w-5 h-5 text-[9px]" : "w-6 h-6 text-[10px]";
    const tip = users.map((u) => u.name || "Someone").join(", ") + " editing…";
    return (
        <div
            className="inline-flex items-center gap-1"
            title={tip}
            data-testid={testid || "typing-chips"}
        >
            <span className="relative inline-flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400" />
            </span>
            <div className="flex -space-x-1.5">
                {shown.map((u) => (
                    <span
                        key={u.user_id}
                        className={`${dim} ${_chipColor(u.user_id)} inline-flex items-center justify-center rounded-full ring-2 ring-[#0a0a0a] font-bold text-white`}
                        data-testid={`typing-chip-${u.user_id}`}
                    >
                        {_initials(u.name)}
                    </span>
                ))}
                {extra > 0 && (
                    <span className={`${dim} bg-slate-600 inline-flex items-center justify-center rounded-full ring-2 ring-[#0a0a0a] font-bold text-white`}>
                        +{extra}
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * iter-124: compact date formatters for the "created" badge shown in
 * the leftmost gutter (desktop) and under the name (mobile). Both are
 * safe against missing / malformed values — return "" so callers can
 * simply gate rendering on truthiness.
 */
function formatCreatedShort(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    // Same calendar year → "Aug 24". Prior years → "Aug 24 '25".
    const monthDay = d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
    });
    return d.getFullYear() === now.getFullYear()
        ? monthDay
        : `${monthDay} '${String(d.getFullYear()).slice(-2)}`;
}

function formatCreatedLong(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}
import {
    TrendingUp,
    ArrowLeft,
    Plus,
    Trash2,
    Loader2,
    Pencil,
    GripVertical,
    X,
    CheckCircle2,
    ArrowRightCircle,
    Palette,
    Share2,
    Settings2,
    ChevronDown,
    Upload,
    Columns3,
    Maximize2,
    RotateCcw,
    ArrowDownNarrowWide,
    ArrowUpNarrowWide,
    Phone,
    MessageCircle,
    Mail,
    ChevronUp,
    UserPlus,
    Square,
    Check,
    UsersRound,
} from "lucide-react";
import ConvertLeadDialog from "../../components/leads-mod/ConvertLeadDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    getLeadColumns,
    saveLeadColumns,
    listLeads,
    createLead,
    updateLead,
    deleteLead,
    importLeadsXps,
    getLeadsStateVersion,
    heartbeatLeadTyping,
    getLeadsTypingState,
} from "../../lib/leads-mod/leads";

// Tiny uuid — we only need it for column ids; collisions on a per-user
// column list are effectively impossible.
function uid() {
    return `col_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

export default function LeadsPage() {
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [convertingRow, setConvertingRow] = useState(null);
    // iter-155 — proper confirm dialog before deleting a lead (used to
    // be a browser-native window.confirm which was easy to miss in the
    // Electron wrapper).
    const [pendingDelete, setPendingDelete] = useState(null);
    // iter-99 / iter-108: client-side sort. `sortKey` is "date" (uses
    // created_at) or "name" (looks at the first column the user has —
    // which is always the Name column on the canonical layout). `sortDir`
    // is desc (newest / Z→A) or asc (oldest / A→Z).
    const [sortKey, setSortKey] = useState("date");
    const [sortDir, setSortDir] = useState("desc");

    // The column we treat as the "Name" column when sorting by name. We
    // pick the first column whose name contains "name" (case-insensitive),
    // falling back to the first column overall — matches the desktop /
    // mobile convention that the first column is the lead's name.
    const nameColumnId = useMemo(() => {
        const byName = columns.find((c) => /name/i.test(c.name || ""));
        return (byName || columns[0])?.id || null;
    }, [columns]);

    // Derived: a copy of `rows` sorted by the active key.
    const sortedRows = useMemo(() => {
        const copy = [...rows];
        if (sortKey === "name" && nameColumnId) {
            copy.sort((a, b) => {
                const av = ((a.cells || {})[nameColumnId] || "").toString().toLowerCase();
                const bv = ((b.cells || {})[nameColumnId] || "").toString().toLowerCase();
                if (!av && !bv) return 0;
                if (!av) return 1;   // blanks always sink
                if (!bv) return -1;
                return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
            });
            return copy;
        }
        copy.sort((a, b) => {
            const ta = a.created_at || "";
            const tb = b.created_at || "";
            if (!ta || !tb) return 0;
            return sortDir === "desc" ? tb.localeCompare(ta) : ta.localeCompare(tb);
        });
        return copy;
    }, [rows, sortKey, sortDir, nameColumnId]);

    const load = async () => {
        setLoading(true);
        try {
            const [cols, leads] = await Promise.all([getLeadColumns(), listLeads()]);
            setColumns(cols);
            setRows(leads);
        } catch {
            toast.error("Couldn't load leads");
        } finally {
            setLoading(false);
        }
    };
    // iter-165 — silent refetch triggered by the version poller; no
    // loading spinner and errors stay quiet so a hiccup doesn't
    // interrupt whatever the user is typing.
    const silentReload = async () => {
        try {
            const [cols, leads] = await Promise.all([getLeadColumns(), listLeads()]);
            setColumns(cols);
            setRows(leads);
        } catch { /* silent */ }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    // iter-165 — cross-teammate sync via a cheap in-memory version
    // counter. We poll every 3s; when the number bumps we silent-reload.
    // Runs while the tab is visible so idle laptops don't keep hitting
    // the API. Matches the pattern used by the Files page.
    useEffect(() => {
        let cancelled = false;
        let lastVersion = -1;
        const tick = async () => {
            if (document.hidden) return;
            try {
                const { version } = await getLeadsStateVersion();
                if (cancelled) return;
                if (lastVersion === -1) { lastVersion = version; return; }
                if (version !== lastVersion) {
                    lastVersion = version;
                    silentReload();
                }
            } catch { /* silent */ }
        };
        const id = setInterval(tick, 3000);
        tick();
        return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // iter-166 — Live cursor chips. Poll who else is typing on each
    // lead row and render a tiny avatar chip. Heartbeats are sent by
    // LeadCell whenever the user focuses/types (see typingRef below).
    const [typingByLead, setTypingByLead] = useState({});
    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            if (document.hidden) return;
            try {
                const { typing } = await getLeadsTypingState();
                if (!cancelled) setTypingByLead(typing || {});
            } catch { /* silent */ }
        };
        const id = setInterval(tick, 2500);
        tick();
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    // iter-166 — throttled heartbeat sender. LeadCell calls this on
    // focus + every keystroke; we dedupe to at most one call every 3s
    // per lead so we don't hammer the API.
    const lastHeartbeatRef = useRef({});
    const sendTypingHeartbeat = (leadId) => {
        if (!leadId) return;
        const now = Date.now();
        const last = lastHeartbeatRef.current[leadId] || 0;
        if (now - last < 3000) return;
        lastHeartbeatRef.current[leadId] = now;
        heartbeatLeadTyping(leadId).catch(() => { /* silent */ });
    };

    const persistColumns = async (next) => {
        setColumns(next); // optimistic
        try {
            await saveLeadColumns(next);
        } catch {
            toast.error("Couldn't save columns");
            load();
        }
    };

    const handleAddColumn = () => {
        const name = window.prompt("Column name:", `Column ${columns.length + 1}`);
        if (!name || !name.trim()) return;
        persistColumns([...columns, { id: uid(), name: name.trim() }]);
    };

    const handleRenameColumn = (colId, newName) => {
        persistColumns(columns.map((c) => (c.id === colId ? { ...c, name: newName } : c)));
    };

    const handleDeleteColumn = (colId) => {
        const col = columns.find((c) => c.id === colId);
        if (!col) return;
        if (!window.confirm(`Delete column "${col.name}"? Data in that column will be hidden (but not erased).`)) return;
        persistColumns(columns.filter((c) => c.id !== colId));
    };

    // Drag-resize column width. We update local state on every move so the
    // user sees the column tracking their pointer in real time, but only
    // round-trip to the server on pointer-up (`commit=true`) so we're not
    // hammering the API while they drag.
    const handleColumnResize = (colId, width, commit = false) => {
        const clamped = Math.max(80, Math.min(800, Math.round(width)));
        const next = columns.map((c) =>
            c.id === colId ? { ...c, width: clamped } : c,
        );
        if (commit) {
            persistColumns(next);
        } else {
            setColumns(next);
        }
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const handleColumnDrag = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = columns.findIndex((c) => c.id === active.id);
        const newIdx = columns.findIndex((c) => c.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        persistColumns(arrayMove(columns, oldIdx, newIdx));
    };

    // ----- Bulk layout actions -----
    // "Reset all" strips every persisted width so columns fall back to their
    // default min-width. "Auto-fit" measures the widest visible cell value
    // (plus the column header) and persists that as the new width, capped to
    // a comfy reading range so a single 200-char address doesn't blow out
    // the whole page.
    const handleResetAllWidths = () => {
        if (columns.length === 0) return;
        const next = columns.map((c) => {
            const { width: _w, ...rest } = c;
            return rest;
        });
        persistColumns(next);
        toast.success("Column widths reset");
    };

    const handleAutoFitWidths = () => {
        if (columns.length === 0 || rows.length === 0) {
            toast.message("Add some leads first, then auto-fit.");
            return;
        }
        // Off-DOM canvas measurement is ~100x faster than spawning hidden
        // <span>s for every cell when the table has hundreds of rows.
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const headerFont = "bold 11px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
        const cellFont = "14px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
        const PADDING = 28; // accounts for px-3 on the cell + grip handle
        const HEADER_EXTRA = 56; // grip icon + rename/delete chrome
        const MIN = 100;
        const MAX = 360; // keep things readable on a wide screen
        const next = columns.map((col) => {
            ctx.font = headerFont;
            const headerWidth = ctx.measureText(col.name || "").width + HEADER_EXTRA;
            ctx.font = cellFont;
            let widest = 0;
            for (const r of rows) {
                const v = (r.cells || {})[col.id];
                if (v == null) continue;
                const s = String(v);
                if (!s) continue;
                const w = ctx.measureText(s).width;
                if (w > widest) widest = w;
            }
            const desired = Math.max(headerWidth, widest + PADDING);
            return { ...col, width: Math.max(MIN, Math.min(MAX, Math.round(desired))) };
        });
        persistColumns(next);
        toast.success("Columns auto-fit to content");
    };

    const handleAddRow = async () => {
        setBusy(true);
        try {
            // iter-108: auto-populate any column literally named "Date"
            // with today's date so newly-created leads sort + filter
            // correctly without the user typing it every time. We do NOT
            // touch "F/U Date" or similarly-named future-date columns.
            const today = new Date();
            const mm = String(today.getMonth() + 1).padStart(2, "0");
            const dd = String(today.getDate()).padStart(2, "0");
            const yyyy = today.getFullYear();
            const todayStr = `${mm}/${dd}/${yyyy}`;
            const seedCells = {};
            for (const col of columns) {
                const nm = (col.name || "").trim().toLowerCase();
                if (nm === "date") seedCells[col.id] = todayStr;
            }
            const r = await createLead(seedCells);
            setRows((prev) => [...prev, r]);
        } catch (err) {
            // iter-152 — surface the real reason instead of a mystery
            // "Couldn't add row" toast. Users on the VPS were hitting
            // this with no clue what was wrong (empty catch swallowed it).
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const detailStr = Array.isArray(detail)
                ? detail.map((d) => d?.msg).filter(Boolean).join("; ")
                : (typeof detail === "string" ? detail : detail?.message);
            const msg = detailStr || err?.message || "Network error";
            toast.error(`Couldn't add row${status ? ` (${status})` : ""}: ${msg}`);
            // eslint-disable-next-line no-console
            console.error("createLead failed", err);
        } finally {
            setBusy(false);
        }
    };

    // ----- XPS import -----
    // Re-uses the Leads parser on the backend. Appends freshly parsed rows
    // to the grid AND merges any previously-unseen columns (e.g. "Locates",
    // "F/U Date") into the user's saved column list so the layout matches
    // the source document.
    const xpsInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const handleImportClick = () => {
        if (xpsInputRef.current) xpsInputRef.current.value = "";
        xpsInputRef.current && xpsInputRef.current.click();
    };
    const handleXpsFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImporting(true);
        const toastId = toast.loading(`Importing ${file.name}…`);
        try {
            const res = await importLeadsXps(file);
            if (Array.isArray(res?.columns)) setColumns(res.columns);
            if (Array.isArray(res?.rows) && res.rows.length) {
                setRows((prev) => [...prev, ...res.rows]);
            }
            toast.success(
                `Imported ${res?.imported_count ?? 0} lead${res?.imported_count === 1 ? "" : "s"} from ${file.name}`
                + (res?.updated_count ? ` · ${res.updated_count} already here were refreshed` : "")
                + (res?.added_columns?.length ? ` · new columns: ${res.added_columns.join(", ")}` : ""),
                { id: toastId, duration: 7000 },
            );
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Import failed";
            toast.error(msg, { id: toastId });
        } finally {
            setImporting(false);
            if (xpsInputRef.current) xpsInputRef.current.value = "";
        }
    };

    const handleCellUpdate = async (leadId, colId, value) => {        setRows((prev) =>
            prev.map((r) =>
                r.id === leadId ? { ...r, cells: { ...(r.cells || {}), [colId]: value } } : r,
            ),
        );
        try {
            await updateLead(leadId, { cells: { [colId]: value } });
        } catch {
            toast.error("Couldn't save cell");
            load();
        }
    };

    // Row tint — the color key is a token from LEAD_ROW_COLORS below. We keep
    // the optimistic update lightweight (local state only, no toast on success)
    // so cycling through swatches feels instant. An empty string clears.
    const handleColorUpdate = async (leadId, color) => {
        setRows((prev) =>
            prev.map((r) => (r.id === leadId ? { ...r, color } : r)),
        );
        try {
            await updateLead(leadId, { color: color || "" });
        } catch {
            toast.error("Couldn't save color");
            load();
        }
    };

    const handleDeleteRow = (leadId) => {
        // iter-155 — open the confirm dialog; the actual delete happens
        // in confirmDelete once the user hits Delete in the AlertDialog.
        const row = rows.find((r) => r.id === leadId);
        setPendingDelete(row || { id: leadId });
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        const leadId = pendingDelete.id;
        setPendingDelete(null);
        try {
            await deleteLead(leadId);
            setRows((prev) => prev.filter((r) => r.id !== leadId));
            toast.success("Lead deleted.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't delete lead.");
        }
    };

    // iter-125: edit the lead's created_at (used by the Newest/Oldest
    // sort). Optimistic — flip local state first so the row jumps in
    // the sort immediately, roll back on failure.
    const handleCreatedAtUpdate = async (leadId, iso) => {
        setRows((prev) =>
            prev.map((r) => (r.id === leadId ? { ...r, created_at: iso } : r)),
        );
        try {
            await updateLead(leadId, { created_at: iso });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save date");
            load();
        }
    };

    const totalCells = useMemo(
        () =>
            rows.reduce(
                (acc, r) => acc + Object.values(r.cells || {}).filter((v) => v && String(v).trim()).length,
                0,
            ),
        [rows],
    );

    // Running total $ across OPEN leads (not yet converted). Sums any cell
    // whose column name hints at money — "value", "amount", "price", "quote",
    // "budget", "total" — tolerating $, commas, spaces, and decimals.
    const openPipelineTotal = useMemo(() => {
        const moneyRegex = /\b(value|amount|price|quote|budget|total|deal)\b/i;
        const moneyCols = columns.filter((c) => moneyRegex.test(c.name || ""));
        if (moneyCols.length === 0) return { total: 0, count: 0, hasMoneyCols: false };
        let total = 0;
        let count = 0;
        for (const r of rows) {
            if (r.converted_project_id) continue;
            for (const c of moneyCols) {
                const raw = (r.cells || {})[c.id];
                if (raw === undefined || raw === null) continue;
                const cleaned = String(raw).replace(/[^0-9.\-]/g, "");
                if (!cleaned) continue;
                const n = parseFloat(cleaned);
                if (Number.isFinite(n)) {
                    total += n;
                    count += 1;
                }
            }
        }
        return { total, count, hasMoneyCols: true };
    }, [rows, columns]);

    const formatMoney = (n) =>
        n.toLocaleString(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
        });

    return (
        <div className="h-screen bg-[#0a0a0a] text-white flex flex-col">
            <main className="flex-1 flex flex-col min-h-0" data-testid="leads-page">
                <header>
                    <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 pb-2 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
                        <div>
                            {window.location.pathname === "/leads-mod" && (
                                <button
                                    type="button"
                                    onClick={() => { window.location.href = "/dashboard"; }}
                                    data-testid="leads-back-dashboard"
                                    title="Back to portal"
                                    className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white/60 hover:text-white hover:border-[#1a3fd1]/60 transition-colors"
                                >
                                    <ArrowLeft className="w-3.5 h-3.5" />
                                    Portal
                                </button>
                            )}
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Sales pipeline</p>
                            <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Leads</h1>
                        </div>
                        <div className="flex min-w-0 flex-wrap items-center gap-3">
                            <span className="hidden whitespace-nowrap font-mono text-xs text-white/40 sm:inline">
                                {rows.length} lead{rows.length === 1 ? "" : "s"} · {columns.length} col{columns.length === 1 ? "" : "s"} · {totalCells} value{totalCells === 1 ? "" : "s"}
                            </span>
                            {openPipelineTotal.hasMoneyCols && openPipelineTotal.count > 0 && (
                                <div
                                    data-testid="leads-open-pipeline-total"
                                    className="inline-flex items-center gap-2 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-3 py-2"
                                    title={`${openPipelineTotal.count} open lead value${openPipelineTotal.count === 1 ? "" : "s"} summed`}
                                >
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8fa4f0]">
                                        Open pipeline
                                    </span>
                                    <span className="text-sm font-bold text-white tabular-nums">
                                        {formatMoney(openPipelineTotal.total)}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <input
                                ref={xpsInputRef}
                                type="file"
                                accept=".xps,.oxps,.pdf,.json,application/json"
                                onChange={handleXpsFile}
                                className="hidden"
                                data-testid="leads-xps-input"
                            />
                            <button
                                onClick={handleImportClick}
                                disabled={importing}
                                data-testid="btn-import-xps"
                                title="Import leads from a .json export or an XPS / OXPS / PDF export — new fields become columns"
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-3 py-2 text-sm font-semibold text-[#8fa4f0] hover:bg-[#1a3fd1]/25 transition-colors disabled:opacity-50 disabled:opacity-50"
                            >
                                {importing ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#8fa4f0]" />
                                ) : (
                                    <Upload className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                )}
                                Import<span className="hidden sm:inline"> JSON / XPS</span>
                            </button>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        data-testid="btn-layout-menu"
                                        title="Layout — reset or auto-fit column widths"
                                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-3 py-2 text-sm font-semibold text-[#8fa4f0] hover:bg-[#1a3fd1]/25 transition-colors disabled:opacity-50"
                                    >
                                        <Columns3 className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                        Layout
                                        <ChevronDown className="w-3 h-3 text-slate-400" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="end"
                                    sideOffset={6}
                                    className="w-64 p-1 bg-[#141414] border-white/15"
                                    data-testid="layout-menu-content"
                                >
                                    <button
                                        type="button"
                                        onClick={handleAutoFitWidths}
                                        data-testid="btn-autofit-widths"
                                        className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-sm hover:bg-white/10 text-left transition-colors"
                                    >
                                        <Maximize2 className="w-4 h-4 text-[#8fa4f0] mt-0.5 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold text-white">
                                                Auto-fit to content
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">
                                                Size each column to its widest visible value.
                                            </div>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResetAllWidths}
                                        data-testid="btn-reset-widths"
                                        className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-sm hover:bg-white/10 text-left transition-colors"
                                    >
                                        <RotateCcw className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold text-white">
                                                Reset all widths
                                            </div>
                                            <div className="text-[10px] text-slate-400 mt-0.5 leading-tight">
                                                Clear every column's saved width and revert to defaults.
                                            </div>
                                        </div>
                                    </button>
                                </PopoverContent>
                            </Popover>
                            <button
                                onClick={handleAddColumn}
                                data-testid="btn-add-column"
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-3 py-2 text-sm font-semibold text-[#8fa4f0] hover:bg-[#1a3fd1]/25 transition-colors disabled:opacity-50"
                            >
                                <Plus className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                Column
                            </button>
                            {/* iter-108: Sort dropdown with name + date axes. */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        data-testid="btn-sort-menu"
                                        title="Sort leads"
                                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#1a3fd1]/50 bg-[#1a3fd1]/15 px-3 py-2 text-sm font-semibold text-[#8fa4f0] hover:bg-[#1a3fd1]/25 transition-colors disabled:opacity-50"
                                    >
                                        {sortDir === "desc" ? (
                                            <ArrowDownNarrowWide className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                        ) : (
                                            <ArrowUpNarrowWide className="w-3.5 h-3.5 text-[#8fa4f0]" />
                                        )}
                                        <span data-testid="btn-sort-label">
                                            {sortKey === "name"
                                                ? (sortDir === "desc" ? "Z→A" : "A→Z")
                                                : (sortDir === "desc" ? "Newest" : "Oldest")}
                                        </span>
                                        <ChevronDown className="w-3 h-3 text-slate-400" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="end"
                                    sideOffset={6}
                                    className="w-56 p-1 bg-[#141414] border-white/15"
                                    data-testid="sort-menu-content"
                                >
                                    {[
                                        { key: "date", dir: "desc", label: "Date — Newest first" },
                                        { key: "date", dir: "asc", label: "Date — Oldest first" },
                                        { key: "name", dir: "asc", label: "Name — A → Z" },
                                        { key: "name", dir: "desc", label: "Name — Z → A" },
                                    ].map((opt) => {
                                        const active = sortKey === opt.key && sortDir === opt.dir;
                                        return (
                                            <button
                                                key={`${opt.key}-${opt.dir}`}
                                                type="button"
                                                onClick={() => { setSortKey(opt.key); setSortDir(opt.dir); }}
                                                data-testid={`btn-sort-${opt.key}-${opt.dir}`}
                                                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-left transition-colors text-xs font-medium ${active ? "bg-[#1a3fd1]/15 text-[#8fa4f0]" : "text-slate-200 hover:bg-white/10 hover:text-white"}`}
                                            >
                                                {opt.dir === "desc" ? (
                                                    <ArrowDownNarrowWide className="w-3.5 h-3.5 shrink-0" />
                                                ) : (
                                                    <ArrowUpNarrowWide className="w-3.5 h-3.5 shrink-0" />
                                                )}
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </PopoverContent>
                            </Popover>
                            <button
                                onClick={handleAddRow}
                                disabled={busy}
                                data-testid="btn-add-lead"
                                className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1a3fd1]/85 transition-colors disabled:opacity-50"
                            >
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                New lead
                            </button>
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="p-10 text-center text-slate-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading leads…
                        </div>
                    ) : (
                        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4 pb-24">
                            {/* Desktop column drag is wrapped in DndContext. The mobile
                                cards section is INTENTIONALLY rendered outside the
                                DndContext — iter-32 fix: @dnd-kit's PointerSensor was
                                swallowing taps on the mobile cards (interpreting each
                                touch as a potential drag start), making every <input>
                                and icon button inert on phones. */}
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleColumnDrag}
                            >
                                <SortableContext
                                    items={columns.map((c) => c.id)}
                                    strategy={horizontalListSortingStrategy}
                                >
                                    {/* Desktop / tablet-landscape: single fit-to-viewport table.
                                        iter-129: no horizontal scroll — columns that don't fit
                                        the width naturally wrap onto a SECOND row per contact
                                        (see topCols / bottomCols split below). Hidden on narrow
                                        phones which use the stacked cards instead. */}
                                    <div className="hidden md:block w-full rounded-sm border border-white/15 bg-white/[0.04] overflow-hidden">
                                        <table
                                            className="border-collapse w-full leads-wrap-table"
                                            data-testid="leads-table"
                                            style={{ tableLayout: "fixed" }}
                                        >
                                            <colgroup>
                                                <col style={{ width: "48px" }} />
                                                {(() => {
                                                    const half = Math.ceil(columns.length / 2) || 1;
                                                    const maxCols = Math.max(half, columns.length - half, 1);
                                                    return Array.from({ length: maxCols }).map((_, i) => (
                                                        <col key={`col-${i}`} />
                                                    ));
                                                })()}
                                                <col style={{ width: "108px" }} />
                                            </colgroup>
                                            <thead className="hidden">
                                                {/* iter-155 — column titles moved inline into each
                                                    lead cell (label above the input). The outer
                                                    header stays in the DOM for a11y + column-drag
                                                    metadata but is visually hidden so users get
                                                    one clear label per cell inside every lead. */}
                                                {(() => {
                                                    const half = Math.ceil(columns.length / 2) || 1;
                                                    const topCols = columns.slice(0, half);
                                                    const bottomCols = columns.slice(half);
                                                    const maxCols = Math.max(topCols.length, bottomCols.length, 1);
                                                    const topPad = maxCols - topCols.length;
                                                    const botPad = maxCols - bottomCols.length;
                                                    return (
                                                        <>
                                                            <tr className="bg-[#141414]">
                                                                <th
                                                                    rowSpan={bottomCols.length > 0 ? 2 : 1}
                                                                    className="w-12 border-r border-white/10 bg-[#141414]"
                                                                    aria-label="Row tint"
                                                                />
                                                                {topCols.map((col) => (
                                                                    <SortableHeader
                                                                        key={col.id}
                                                                        column={col}
                                                                        onRename={handleRenameColumn}
                                                                        onDelete={handleDeleteColumn}
                                                                        onResize={handleColumnResize}
                                                                    />
                                                                ))}
                                                                {Array.from({ length: topPad }).map((_, i) => (
                                                                    <th key={`toppad-${i}`} className="bg-[#141414]" />
                                                                ))}
                                                                <th
                                                                    rowSpan={bottomCols.length > 0 ? 2 : 1}
                                                                    className="w-[108px] border-l border-white/15 bg-[#141414]"
                                                                />
                                                            </tr>
                                                            {bottomCols.length > 0 && (
                                                                <tr className="bg-[#141414] border-b border-white/15">
                                                                    {bottomCols.map((col) => (
                                                                        <SortableHeader
                                                                            key={col.id}
                                                                            column={col}
                                                                            onRename={handleRenameColumn}
                                                                            onDelete={handleDeleteColumn}
                                                                            onResize={handleColumnResize}
                                                                        />
                                                                    ))}
                                                                    {Array.from({ length: botPad }).map((_, i) => (
                                                                        <th key={`botpad-${i}`} className="bg-[#141414]" />
                                                                    ))}
                                                                </tr>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </thead>
                                            <tbody>
                                                {rows.length === 0 ? (
                                                    <tr>
                                                        <td
                                                            colSpan={Math.max(Math.ceil(columns.length / 2), 1) + 2}
                                                            className="px-4 py-10 text-center text-sm text-slate-500"
                                                        >
                                                            No leads yet. Click <span className="text-[#8fa4f0] font-bold">New lead</span> to start typing.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    sortedRows.map((row) => (
                                                        <LeadRow
                                                            key={row.id}
                                                            row={row}
                                                            columns={columns}
                                                            typingUsers={typingByLead[row.id] || []}
                                                            onTypingHeartbeat={sendTypingHeartbeat}
                                                            onCellUpdate={(colId, value) =>
                                                                handleCellUpdate(row.id, colId, value)
                                                            }
                                                            onColorChange={(c) =>
                                                                handleColorUpdate(row.id, c)
                                                            }
                                                            onCreatedAtChange={handleCreatedAtUpdate}
                                                            onDelete={() => handleDeleteRow(row.id)}
                                                            onConvert={() => setConvertingRow(row)}
                                                            converted={!!row.converted_project_id}
                                                        />
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </SortableContext>
                            </DndContext>

                            {/* iter-129: MobileColumnManager removed — its
                                collapsible "Edit columns" bar looked visually
                                identical to each lead card's own "Show/Hide N
                                more fields" toggle, which the user (rightly)
                                called out as a duplicate. Column renames /
                                deletes live on the desktop table now. */}

                            {/* Mobile-only stacked cards — no horizontal scroll. */}
                            <div className="md:hidden space-y-3" data-testid="leads-cards-mobile">
                                {rows.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-sm text-slate-500 rounded-md border border-white/15 bg-white/[0.04]">
                                        No leads yet. Tap <span className="text-[#8fa4f0] font-bold">New lead</span> to start.
                                    </div>
                                ) : (
                                    sortedRows.map((row) => (
                                        <LeadCardMobile
                                            key={row.id}
                                            row={row}
                                            columns={columns}
                                            typingUsers={typingByLead[row.id] || []}
                                            onTypingHeartbeat={sendTypingHeartbeat}
                                            onCellUpdate={(colId, value) =>
                                                handleCellUpdate(row.id, colId, value)
                                            }
                                            onColorChange={(c) =>
                                                handleColorUpdate(row.id, c)
                                            }
                                            onCreatedAtChange={handleCreatedAtUpdate}
                                            onDelete={() => handleDeleteRow(row.id)}
                                            onConvert={() => setConvertingRow(row)}
                                            converted={!!row.converted_project_id}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <ConvertLeadDialog
                open={!!convertingRow}
                onOpenChange={(o) => !o && setConvertingRow(null)}
                lead={convertingRow}
                columns={columns}
                onConverted={() => {
                    setConvertingRow(null);
                    load();
                    // Contained mode: the project lives inside the leads
                    // module (lm_projects) — no host workspace to jump to yet.
                }}
            />

            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && setPendingDelete(null)}
            >
                <AlertDialogContent className="bg-[#111] border-white/15" data-testid="lead-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this lead?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            {pendingDelete && (
                                <>
                                    You're about to permanently delete
                                    {" "}
                                    <span className="text-white font-bold">
                                        {(columns[0] && pendingDelete.cells?.[columns[0].id]) || "this lead"}
                                    </span>
                                    {" "}and everything typed into it. This can't be undone.
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            className="bg-[#1a1a1a] border-white/15 text-slate-200"
                            data-testid="lead-delete-cancel"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            data-testid="lead-delete-confirm-btn"
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                        >
                            Delete lead
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SortableHeader({ column, onRename, onDelete, onResize }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: column.id });
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(column.name);
    const inputRef = useRef(null);
    const thRef = useRef(null);
    const [resizing, setResizing] = useState(false);

    useEffect(() => setDraft(column.name), [column.name]);
    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    // Pointer-driven resize. We capture the pointer on mousedown so the
    // browser keeps firing pointermove events on us even when the cursor
    // strays outside the handle (otherwise dragging fast off the column
    // would orphan the resize). Persist on pointerup.
    const startResize = (e) => {
        if (!onResize || !thRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = thRef.current.getBoundingClientRect().width;
        setResizing(true);
        const handleMove = (ev) => {
            const next = startWidth + (ev.clientX - startX);
            onResize(column.id, next, false);
        };
        const handleUp = (ev) => {
            const next = startWidth + (ev.clientX - startX);
            onResize(column.id, next, true);
            setResizing(false);
            window.removeEventListener("pointermove", handleMove);
            window.removeEventListener("pointerup", handleUp);
        };
        window.addEventListener("pointermove", handleMove);
        window.addEventListener("pointerup", handleUp);
    };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
        // iter-129: widths now come from the table's auto-distribution
        // so wrapped rows stay fully visible on any screen. Custom
        // per-column widths are ignored on desktop; the table takes
        // 100% of the container and splits equally across visible cols.
    };

    const commit = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed && trimmed !== column.name) onRename(column.id, trimmed);
        else setDraft(column.name);
    };

    return (
        <th
            ref={(node) => {
                setNodeRef(node);
                thRef.current = node;
            }}
            style={style}
            className="group/col relative border-l border-white/15 first:border-l-0 px-3 py-2 text-left text-[11px] uppercase tracking-wider font-bold text-slate-300 select-none"
            data-testid={`leads-col-${column.id}`}
        >
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    {...attributes}
                    {...listeners}
                    aria-label="Drag column"
                    data-testid={`leads-col-drag-${column.id}`}
                    className="shrink-0 w-4 flex items-center justify-center text-slate-500 hover:text-[#8fa4f0] cursor-grab active:cursor-grabbing touch-none"
                >
                    <GripVertical className="w-3.5 h-3.5" />
                </button>
                {editing ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commit(); }
                            if (e.key === "Escape") { setEditing(false); setDraft(column.name); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`leads-col-input-${column.id}`}
                        className="flex-1 bg-[#0a0a0a] border border-[#1a3fd1]/60 rounded-sm px-1 py-0.5 text-white text-xs outline-none"
                    />
                ) : (
                    <span
                        className="flex-1 truncate cursor-pointer hover:text-white"
                        onDoubleClick={() => setEditing(true)}
                        title="Double-click to rename"
                    >
                        {column.name}
                    </span>
                )}
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    aria-label="Rename column"
                    data-testid={`leads-col-rename-${column.id}`}
                    className="shrink-0 w-4 h-4 flex items-center justify-center text-slate-500 hover:text-[#8fa4f0] opacity-0 group-hover/col:opacity-100 transition-opacity"
                >
                    <Pencil className="w-3 h-3" />
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(column.id)}
                    aria-label="Delete column"
                    data-testid={`leads-col-delete-${column.id}`}
                    className="shrink-0 w-4 h-4 flex items-center justify-center text-slate-500 hover:text-red-400 opacity-0 group-hover/col:opacity-100 transition-opacity"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
            {/* Resize grip — sits flush against the right edge of the
                header cell. `onPointerDown` initiates a window-level
                pointer-capture loop so dragging works even when the
                cursor leaves the grip mid-drag. The 8px hit area is wide
                enough to grab on a tablet without overlapping the next
                column's content. */}
            {onResize && (
                <span
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize column"
                    data-testid={`leads-col-resize-${column.id}`}
                    onPointerDown={startResize}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        // Double-click to reset to default width.
                        onResize(column.id, 140, true);
                    }}
                    className={`absolute top-0 right-0 h-full w-2 cursor-col-resize select-none flex items-center justify-center group/resize ${
                        resizing ? "bg-[#1a3fd1]/40" : "hover:bg-[#1a3fd1]/30"
                    }`}
                    title="Drag to resize · double-click to reset"
                >
                    <span
                        className={`block w-px h-3/5 ${
                            resizing
                                ? "bg-[#1a3fd1]"
                                : "bg-[#1a1a1a] group-hover/resize:bg-[#1a3fd1]"
                        }`}
                    />
                </span>
            )}
        </th>
    );
}

function LeadActionButtons({ row, onColorChange, onDelete, onConvert, converted, paletteOpen, setPaletteOpen, className = "" }) {
    return (
        <div className={`inline-flex items-center gap-0.5 flex-wrap justify-center ${className}`}>
            {/* Color picker — shadcn Popover auto-flips + clamps to viewport
                so it can't be clipped off-screen on narrow phones. */}
            <Popover open={paletteOpen} onOpenChange={setPaletteOpen}>
                <PopoverTrigger asChild>
                    <button
                        data-testid={`lead-color-toggle-${row.id}`}
                        aria-label="Change row color"
                        title="Row color"
                        className={`w-6 h-6 inline-flex items-center justify-center rounded border border-white/15 hover:border-[#1a3fd1]/60 text-slate-400 hover:text-[#8fa4f0] transition-colors ${
                            row.color ? "opacity-100" : ""
                        }`}
                    >
                        {row.color ? (
                            <span
                                className={`w-3.5 h-3.5 rounded-full ${
                                    _COLOR_BY_KEY[row.color]?.swatch || "bg-slate-500"
                                }`}
                            />
                        ) : (
                            <Palette className="w-4 h-4" />
                        )}
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    sideOffset={6}
                    className="w-auto p-2 bg-[#141414] border-white/15"
                    data-testid={`lead-color-palette-${row.id}`}
                >
                    <div className="flex items-center gap-1.5">
                        {LEAD_ROW_COLORS.map((c) => {
                            const active = (row.color || "") === c.key;
                            return (
                                <button
                                    key={c.key || "none"}
                                    onClick={() => {
                                        onColorChange(c.key);
                                        setPaletteOpen(false);
                                    }}
                                    title={c.label}
                                    data-testid={`lead-color-${row.id}-${c.key || "clear"}`}
                                    className={`w-6 h-6 rounded-full ${c.swatch} ${
                                        active
                                            ? "ring-2 ring-white ring-offset-2 ring-offset-[#141414]"
                                            : "hover:ring-2 hover:ring-white/50 hover:ring-offset-2 hover:ring-offset-[#141414]"
                                    } transition`}
                                />
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
            {converted ? (
                <span
                    title="Converted to project"
                    data-testid={`lead-converted-${row.id}`}
                    className="inline-flex items-center justify-center w-8 h-8 text-emerald-400"
                >
                    <CheckCircle2 className="w-4 h-4" />
                </span>
            ) : (
                <button
                    onClick={onConvert}
                    data-testid={`lead-convert-${row.id}`}
                    aria-label="Convert lead to project"
                    title="Sign & convert to project"
                    className="w-6 h-6 inline-flex items-center justify-center rounded border border-white/15 hover:border-[#1a3fd1]/60 text-slate-400 hover:text-[#8fa4f0] transition-colors"
                >
                    <ArrowRightCircle className="w-3.5 h-3.5" />
                </button>
            )}
            <button
                onClick={onDelete}
                data-testid={`lead-delete-${row.id}`}
                aria-label="Delete row"
                title="Delete this lead"
                className="w-6 h-6 inline-flex items-center justify-center rounded border border-rose-500/50 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 transition-colors"
            >
                <Trash2 className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}

/**
 * iter-125: click the created-date badge on a lead → opens a native
 * date picker. Uses <input type="date"> for max compatibility (mobile
 * and desktop both give a proper OS-native picker) rather than shipping
 * a heavier Radix Calendar + Popover. The visible chip stays a compact
 * "Aug 24" label; the invisible <input> sits absolutely on top of the
 * chip so a tap opens the picker.
 */
function CreatedDateChip({ iso, onChange, testid, layout = "stacked" }) {
    const short = formatCreatedShort(iso);
    const long = formatCreatedLong(iso);
    const isoDay = iso ? String(iso).slice(0, 10) : "";
    if (!short && !onChange) return null;
    return (
        <span
            className={`relative inline-block ${
                layout === "inline"
                    ? "text-[10px] text-slate-500"
                    : "text-[9px] uppercase tracking-wider text-slate-500 leading-none"
            }`}
            title={long ? `Created ${long} — click to edit` : "Set created date"}
            data-testid={testid}
        >
            {layout === "inline" ? `Created ${short || "—"}` : (short || "—")}
            <input
                type="date"
                value={isoDay}
                onChange={(e) => {
                    const v = e.target.value;
                    if (!v) return;
                    onChange(v);
                }}
                className="absolute inset-0 opacity-0 cursor-pointer"
                aria-label="Edit created date"
                data-testid={testid ? `${testid}-input` : undefined}
            />
        </span>
    );
}

function LeadRow({ row, columns, typingUsers = [], onTypingHeartbeat, onCellUpdate, onColorChange, onDelete, onConvert, onCreatedAtChange, converted }) {
    const [paletteOpen, setPaletteOpen] = useState(false);
    // iter-124: user-visible "created" date in the leftmost gutter so
    // the current Newest/Oldest sort key has a face. Uses the existing
    // `created_at` ISO string (already server-populated on every
    // insert) — no new backend field required.
    const createdShort = formatCreatedShort(row.created_at);
    const createdLong = formatCreatedLong(row.created_at);
    // iter-129: split columns 50/50 so a wide list wraps onto a
    // second row per contact instead of causing horizontal scroll.
    const half = Math.ceil(columns.length / 2) || 1;
    const topCols = columns.slice(0, half);
    const bottomCols = columns.slice(half);
    const maxCols = Math.max(topCols.length, bottomCols.length, 1);
    const topPad = maxCols - topCols.length;
    const botPad = maxCols - bottomCols.length;
    const bgClass = rowBgClass(row.color) || "hover:bg-white/5/50";
    const hasBottom = bottomCols.length > 0;
    return (
        <>
            <tr
                className={`group/row border-t border-white/10 transition-colors ${bgClass} ${
                    hasBottom ? "leads-row-top" : ""
                }`}
                data-testid={`lead-row-${row.id}`}
                data-color={row.color || ""}
            >
                {/* Leftmost gutter — quick red ("Lost") / green ("Won")
                    row-tint toggles, plus the created-date badge below.
                    Spans both wrapped lines so it visually anchors the
                    whole contact. */}
                <td
                    rowSpan={hasBottom ? 2 : 1}
                    className="w-12 border-r border-white/10 align-middle"
                >
                    <div className="flex flex-col items-center justify-center gap-1 py-1">
                        <button
                            type="button"
                            onClick={() => onColorChange(row.color === "emerald" ? "" : "emerald")}
                            data-testid={`lead-quick-green-${row.id}`}
                            aria-label={row.color === "emerald" ? "Clear green tint" : "Mark row green (Won)"}
                            title={row.color === "emerald" ? "Clear tint" : "Mark as Won (green)"}
                            className={`w-4 h-4 rounded-full transition-all ${
                                row.color === "emerald"
                                    ? "bg-emerald-500 ring-2 ring-white/70 ring-offset-1 ring-offset-[#0a0a0a]"
                                    : "bg-emerald-500/40 hover:bg-emerald-500 hover:scale-110"
                            }`}
                        />
                        <button
                            type="button"
                            onClick={() => onColorChange(row.color === "rose" ? "" : "rose")}
                            data-testid={`lead-quick-red-${row.id}`}
                            aria-label={row.color === "rose" ? "Clear red tint" : "Mark row red (Lost)"}
                            title={row.color === "rose" ? "Clear tint" : "Mark as Lost (red)"}
                            className={`w-4 h-4 rounded-full transition-all ${
                                row.color === "rose"
                                    ? "bg-rose-500 ring-2 ring-white/70 ring-offset-1 ring-offset-[#0a0a0a]"
                                    : "bg-rose-500/40 hover:bg-rose-500 hover:scale-110"
                            }`}
                        />
                        {(createdShort || onCreatedAtChange) && (
                            <CreatedDateChip
                                iso={row.created_at}
                                onChange={(v) => onCreatedAtChange(row.id, v)}
                                testid={`lead-row-created-${row.id}`}
                            />
                        )}
                        {typingUsers.length > 0 && (
                            <TypingChips
                                users={typingUsers}
                                testid={`lead-row-typing-${row.id}`}
                            />
                        )}
                    </div>
                </td>
                {topCols.map((col) => (
                    <LeadCell
                        key={col.id}
                        rowId={row.id}
                        column={col}
                        value={(row.cells || {})[col.id] || ""}
                        onSave={(v) => onCellUpdate(col.id, v)}
                        onTypingHeartbeat={onTypingHeartbeat}
                    />
                ))}
                {Array.from({ length: topPad }).map((_, i) => (
                    <td key={`toppad-${i}`} className="border-l border-white/10" />
                ))}
                <td
                    rowSpan={hasBottom ? 2 : 1}
                    className="w-[108px] border-l border-white/10 text-center align-middle px-1"
                >
                    <LeadActionButtons
                        row={row}
                        onColorChange={onColorChange}
                        onDelete={onDelete}
                        onConvert={onConvert}
                        converted={converted}
                        paletteOpen={paletteOpen}
                        setPaletteOpen={setPaletteOpen}
                    />
                </td>
            </tr>
            {hasBottom && (
                <tr
                    className={`group/row transition-colors ${bgClass} leads-row-bot`}
                    data-testid={`lead-row-${row.id}-cont`}
                    data-color={row.color || ""}
                >
                    {bottomCols.map((col) => (
                        <LeadCell
                            key={col.id}
                            rowId={row.id}
                            column={col}
                            value={(row.cells || {})[col.id] || ""}
                            onSave={(v) => onCellUpdate(col.id, v)}
                            onTypingHeartbeat={onTypingHeartbeat}
                        />
                    ))}
                    {Array.from({ length: botPad }).map((_, i) => (
                        <td key={`botpad-${i}`} className="border-l border-white/10" />
                    ))}
                </tr>
            )}
        </>
    );
}

/**
 * Mobile-first stacked card for a lead row. Used at < md breakpoint instead
 * of the wide table — no horizontal scroll, full-width row-color tint, all
 * action buttons always visible (tap-first UX).
 */
function LeadCardMobile({ row, columns, typingUsers = [], onTypingHeartbeat, onCellUpdate, onColorChange, onDelete, onConvert, onCreatedAtChange, converted }) {
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    // iter-124: created date shown under the name so mobile users can
    // see WHY the newest sort order looks the way it does.
    const createdShort = formatCreatedShort(row.created_at);

    // iter-99: figure out which columns are the Name, Status, Phone, and
    // Email so we can give them special UI affordances (status pill,
    // tel:/sms:/mailto: launch buttons). The first column is always
    // treated as Name (matches the desktop convention). Status / Phone /
    // Email are matched by case-insensitive name fragments so user
    // renames like "Phone Number" or "Project status" still hit.
    const nameCol = columns[0] || null;
    const statusCol = useMemo(
        () =>
            columns.find(
                (c, i) =>
                    i > 0 && (c.name || "").toLowerCase().includes("status"),
            ) || null,
        [columns],
    );

    const PRIORITY_KEYWORDS = ["address", "phone", "status"];
    const isPriorityCol = (col, idx) => {
        if (idx === 0) return true;
        if (statusCol && col.id === statusCol.id) return true; // status renders inline w/ name
        const n = (col.name || "").toLowerCase();
        return PRIORITY_KEYWORDS.some((kw) => n.includes(kw));
    };
    // visibleCols excludes the name column (rendered in header) and the
    // status column (rendered as the 1-letter pill) so we don't render
    // them twice when expanded.
    const visibleCols = (expanded
        ? columns
        : columns.filter((c, i) => isPriorityCol(c, i))
    ).filter(
        (c) => c.id !== (nameCol && nameCol.id) && c.id !== (statusCol && statusCol.id),
    );
    // iter-122: `hiddenCount` used to double as both "how many extra
    // fields exist" AND "show the collapse button". When expanded it
    // computed to zero, so the collapse button vanished — the user
    // had no way to shrink the card back. Split into two values:
    //   * expandableCount: constant total of non-priority fields
    //   * hiddenCount:     how many are currently NOT rendered
    const totalExtraCols = columns.filter(
        (c, i) =>
            !isPriorityCol(c, i) &&
            c.id !== (nameCol && nameCol.id) &&
            c.id !== (statusCol && statusCol.id),
    ).length;
    const hiddenCount = expanded ? 0 : totalExtraCols;

    const nameValue = nameCol ? (row.cells || {})[nameCol.id] || "" : "";
    const statusValue = statusCol ? (row.cells || {})[statusCol.id] || "" : "";

    return (
        <div
            className={`group/row rounded-md border border-white/10 transition-colors p-2 space-y-1.5 ${
                rowBgClass(row.color) || "bg-white/[0.05]"
            }`}
            data-testid={`lead-card-${row.id}`}
            data-color={row.color || ""}
            data-expanded={expanded ? "true" : "false"}
        >
            {/* iter-99: name + status pill share the top row so the most
                scannable fields are above the fold. Status is a single
                tappable letter that opens an inline editor. */}
            {nameCol && (
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                        {expanded && (
                            <label className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold block mb-0.5">
                                {nameCol.name}
                            </label>
                        )}
                        <LeadCell
                            rowId={row.id}
                            column={nameCol}
                            value={nameValue}
                            onSave={(v) => onCellUpdate(nameCol.id, v)}
                            onTypingHeartbeat={onTypingHeartbeat}
                            mobile
                        />
                        {(createdShort || onCreatedAtChange) && (
                            <div className="mt-0.5">
                                <CreatedDateChip
                                    iso={row.created_at}
                                    onChange={(v) => onCreatedAtChange(row.id, v)}
                                    layout="inline"
                                    testid={`lead-card-created-${row.id}`}
                                />
                            </div>
                        )}
                        {typingUsers.length > 0 && (
                            <div className="mt-1">
                                <TypingChips
                                    users={typingUsers}
                                    testid={`lead-card-typing-${row.id}`}
                                />
                            </div>
                        )}
                    </div>
                    {statusCol && (
                        <div className="shrink-0 w-9">
                            {expanded && (
                                <label className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold block mb-0.5 text-center">
                                    {statusCol.name.charAt(0).toUpperCase()}
                                </label>
                            )}
                            <StatusPill
                                rowId={row.id}
                                value={statusValue}
                                onSave={(v) => onCellUpdate(statusCol.id, v)}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* iter-122: collapse button now shows whenever the card
                has extra fields available AND we're expanded — the
                old `hiddenCount > 0` gate hid it exactly when it
                was needed. */}
            {expanded && totalExtraCols > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    data-testid={`lead-card-collapse-top-${row.id}`}
                    aria-label="Hide extra fields"
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-sm bg-white/[0.05] hover:bg-[#1a3fd1]/15 border border-[#1a3fd1]/40 text-[10px] uppercase tracking-[0.2em] font-bold text-[#8fa4f0] transition-colors"
                >
                    <ChevronUp className="w-3 h-3" />
                    Hide {totalExtraCols} field{totalExtraCols === 1 ? "" : "s"}
                </button>
            )}

            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0 space-y-1.5">
                    {visibleCols.map((col) => (
                        <div key={col.id}>
                            {/* iter-122: collapsed cards ditch the tiny
                                field labels — the icons in the LeadCell
                                (phone / mail / map pin) already scream
                                "this is a phone number", so a redundant
                                "PHONE NUMBER" caption above just wastes
                                vertical space on already-cramped phones. */}
                            {expanded && (
                                <label className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold block mb-0.5">
                                    {col.name}
                                </label>
                            )}
                            <LeadCell
                                rowId={row.id}
                                column={col}
                                value={(row.cells || {})[col.id] || ""}
                                onSave={(v) => onCellUpdate(col.id, v)}
                                onTypingHeartbeat={onTypingHeartbeat}
                                mobile
                            />
                        </div>
                    ))}
                </div>
                <LeadActionButtons
                    row={row}
                    onColorChange={onColorChange}
                    onDelete={onDelete}
                    onConvert={onConvert}
                    converted={converted}
                    paletteOpen={paletteOpen}
                    setPaletteOpen={setPaletteOpen}
                    className="flex-col shrink-0"
                />
            </div>
            {/* Bottom expand/collapse toggle — always visible when the
                lead has extra fields available (iter-122 fix). */}
            {totalExtraCols > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`lead-card-toggle-${row.id}`}
                    aria-expanded={expanded}
                    className={`w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm border border-dashed text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
                        expanded
                            ? "border-[#1a3fd1]/60 text-[#8fa4f0] hover:bg-[#1a3fd1]/15"
                            : "border-white/10 text-slate-400 hover:text-[#8fa4f0] hover:border-[#1a3fd1]/60"
                    }`}
                >
                    {expanded ? (
                        <>
                            <ChevronUp className="w-3 h-3 transition-transform" />
                            Hide {totalExtraCols} field{totalExtraCols === 1 ? "" : "s"}
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3 transition-transform" />
                            Show {totalExtraCols} more field{totalExtraCols === 1 ? "" : "s"}
                        </>
                    )}
                </button>
            )}
        </div>
    );
}

/**
 * iter-99 — Compact 1-letter status indicator. Tap to open an inline
 * editor; first non-whitespace letter is shown in the pill. Coloured
 * green when populated, slate when empty so the contractor can see
 * uncategorised leads at a glance.
 */
function StatusPill({ rowId, value, onSave }) {
    const [open, setOpen] = useState(false);
    const [local, setLocal] = useState(value);
    useEffect(() => setLocal(value), [value]);
    const letter = (value || "").trim().charAt(0).toUpperCase() || "—";
    const filled = !!(value && value.trim());

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={`lead-status-pill-${rowId}`}
                    aria-label={`Status: ${value || "not set"}`}
                    title={value || "Set status"}
                    className={`w-9 h-9 inline-flex items-center justify-center rounded-sm border text-sm font-bold transition-colors ${
                        filled
                            ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-200 hover:bg-emerald-500/25"
                            : "bg-[#0a0a0a] border-white/15 text-slate-500 hover:border-[#1a3fd1]/60 hover:text-[#8fa4f0]"
                    }`}
                >
                    {letter}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-56 p-2 bg-[#141414] border-white/15"
            >
                <label className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold block mb-1">
                    Status
                </label>
                <input
                    autoFocus
                    type="text"
                    value={local}
                    onChange={(e) => setLocal(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            if (local !== value) onSave(local);
                            setOpen(false);
                        }
                        if (e.key === "Escape") {
                            setLocal(value);
                            setOpen(false);
                        }
                    }}
                    onBlur={() => {
                        if (local !== value) onSave(local);
                    }}
                    placeholder="e.g. Hot, Quoted, Won"
                    data-testid={`lead-status-input-${rowId}`}
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#1a3fd1]/60"
                />
            </PopoverContent>
        </Popover>
    );
}

// iter-99 — Detect when a column should sprout contact-launch icons
// (tel: / sms: / mailto:). We match on name fragments so "Phone Number",
// "Mobile Phone", "Cell", "Email Address" etc. all light up.
const PHONE_KEYWORDS = ["phone", "mobile", "cell", "tel"];
const EMAIL_KEYWORDS = ["email", "e-mail", "mail"];

function detectContactKind(columnName) {
    const n = (columnName || "").toLowerCase();
    if (PHONE_KEYWORDS.some((k) => n.includes(k))) return "phone";
    if (EMAIL_KEYWORDS.some((k) => n.includes(k))) return "email";
    return null;
}

function sanitizePhone(value) {
    // tel: / sms: URIs accept digits, +, *, # — drop everything else.
    return (value || "").replace(/[^0-9+*#]/g, "");
}

/**
 * Inline launch icons attached to phone / email lead cells. Pure
 * client-side — uses platform handlers (`tel:`, `sms:`, `mailto:`) so it
 * just works on mobile (dials/composes) and gracefully degrades on
 * desktop (opens default mail client / shows nothing).
 */
export function ContactLaunchIcons({ value, kind, testIdRoot, compact = false }) {
    const trimmed = (value || "").trim();
    if (!trimmed) return null;
    const sz = compact ? "w-3 h-3" : "w-3.5 h-3.5";
    const btn = "p-1.5 rounded-sm border border-white/10 hover:border-[#1a3fd1]/60 hover:text-[#8fa4f0] text-slate-300 transition-colors";
    if (kind === "phone") {
        const tel = sanitizePhone(trimmed);
        if (!tel) return null;
        return (
            <div className="flex items-center gap-1 shrink-0">
                <a
                    href={`tel:${tel}`}
                    data-testid={`${testIdRoot}-call`}
                    aria-label={`Call ${trimmed}`}
                    title={`Call ${trimmed}`}
                    className={btn}
                >
                    <Phone className={sz} />
                </a>
                <a
                    href={`sms:${tel}`}
                    data-testid={`${testIdRoot}-sms`}
                    aria-label={`Text ${trimmed}`}
                    title={`Text ${trimmed}`}
                    className={btn}
                >
                    <MessageCircle className={sz} />
                </a>
            </div>
        );
    }
    if (kind === "email") {
        return (
            <a
                href={`mailto:${trimmed}`}
                data-testid={`${testIdRoot}-email`}
                aria-label={`Email ${trimmed}`}
                title={`Email ${trimmed}`}
                className={`${btn} shrink-0`}
            >
                <Mail className={sz} />
            </a>
        );
    }
    return null;
}

/**
 * iter-130: Multi-phone editor. Phone-typed cells store multiple
 * numbers as newline-separated strings so users can capture a mobile
 * + work + home line for a single lead without polluting the column
 * grid. Each entry gets its own tel:/sms: launcher; a `+` button
 * appends a fresh line. Backwards-compatible — single-line legacy
 * values are treated as one entry.
 */
function MultiPhoneCell({ rowId, column, value, onSave, mobile = false, onTypingHeartbeat }) {
    const beat = () => { if (onTypingHeartbeat) onTypingHeartbeat(rowId); };
    const initial = useMemo(() => {
        const parts = (value || "").split(/\r?\n/);
        return parts.length ? parts : [""];
    }, [value]);
    const [entries, setEntries] = useState(initial);
    useEffect(() => setEntries(initial), [initial]);

    const commit = (next) => {
        const cleaned = next.map((s) => s.trim()).filter((s, i, arr) => s || i === arr.length - 1 && arr.length === 1);
        const joined = cleaned.join("\n");
        if (joined !== (value || "")) onSave(joined);
    };

    const updateEntry = (idx, v) => {
        setEntries((prev) => prev.map((e, i) => (i === idx ? v : e)));
    };
    const addEntry = () => {
        setEntries((prev) => [...prev, ""]);
    };
    const removeEntry = (idx) => {
        const next = entries.filter((_, i) => i !== idx);
        const safe = next.length ? next : [""];
        setEntries(safe);
        commit(safe);
    };

    const inputCls = mobile
        ? "flex-1 min-w-0 bg-[#0a0a0a] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#1a3fd1]/60 focus:bg-[#1a3fd1]/5"
        : "flex-1 min-w-0 bg-transparent px-2 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:bg-[#1a3fd1]/5 focus:ring-1 focus:ring-[#1a3fd1]/40";

    const body = (
        <div className="flex flex-col gap-1 w-full">
            {entries.map((entry, idx) => (
                <div
                    key={idx}
                    className="flex items-center gap-1"
                    data-testid={`lead-phone-row-${rowId}-${column.id}-${idx}`}
                >
                    <input
                        type="text"
                        value={entry}
                        onChange={(e) => { updateEntry(idx, e.target.value); beat(); }}
                        onFocus={beat}
                        onBlur={() => commit(entries)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                            if (e.key === "Escape") { setEntries(initial); e.target.blur(); }
                        }}
                        data-testid={
                            idx === 0
                                ? `lead-cell-${rowId}-${column.id}`
                                : `lead-cell-${rowId}-${column.id}-${idx}`
                        }
                        className={inputCls}
                        placeholder={idx === 0 ? "—" : "+ additional number"}
                    />
                    <ContactLaunchIcons
                        value={entry}
                        kind="phone"
                        testIdRoot={`lead-cell-${rowId}-${column.id}-${idx}`}
                        compact
                    />
                    {entries.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removeEntry(idx)}
                            data-testid={`lead-phone-remove-${rowId}-${column.id}-${idx}`}
                            aria-label="Remove this number"
                            title="Remove this number"
                            className="shrink-0 p-1.5 rounded-sm border border-transparent text-slate-500 hover:text-rose-400 hover:border-rose-400/60 transition-colors"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    )}
                </div>
            ))}
            <button
                type="button"
                onClick={addEntry}
                data-testid={`lead-phone-add-${rowId}-${column.id}`}
                aria-label="Add another phone number"
                title="Add another phone number"
                className="self-start inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-[#8fa4f0] transition-colors"
            >
                <Plus className="w-3 h-3" /> Add number
            </button>
        </div>
    );

    if (mobile) return body;
    return (
        <td className="border-l border-white/10 first:border-l-0 p-1 align-top">
            {body}
        </td>
    );
}

function LeadCell({ rowId, column, value, onSave, mobile = false, onTypingHeartbeat }) {
    const [local, setLocal] = useState(value);
    useEffect(() => setLocal(value), [value]);
    // iter-99: phone/email columns get tel:/sms:/mailto: launch icons
    // glued onto the right of the input.
    const contactKind = detectContactKind(column.name);
    // iter-166 — heartbeat helper. Called on focus + every keystroke;
    // the parent throttles to at most one call per 3s per lead.
    const beat = () => { if (onTypingHeartbeat) onTypingHeartbeat(rowId); };
    // iter-130: phone cells become a stacked multi-number editor so
    // users can add secondary numbers with a "+" button.
    if (contactKind === "phone") {
        return (
            <MultiPhoneCell
                rowId={rowId}
                column={column}
                value={value}
                onSave={onSave}
                mobile={mobile}
                onTypingHeartbeat={onTypingHeartbeat}
            />
        );
    }
    if (mobile) {
        return (
            <div className="flex items-center gap-1.5">
                <input
                    type="text"
                    value={local}
                    onChange={(e) => { setLocal(e.target.value); beat(); }}
                    onFocus={beat}
                    onBlur={() => { if (local !== value) onSave(local); }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                        if (e.key === "Escape") { setLocal(value); e.target.blur(); }
                    }}
                    data-testid={`lead-cell-${rowId}-${column.id}`}
                    className="flex-1 min-w-0 bg-[#0a0a0a] border border-white/10 rounded-sm px-2 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#1a3fd1]/60 focus:bg-[#1a3fd1]/5"
                    placeholder="—"
                />
                {contactKind && (
                    <ContactLaunchIcons
                        value={value}
                        kind={contactKind}
                        testIdRoot={`lead-cell-${rowId}-${column.id}`}
                        compact
                    />
                )}
            </div>
        );
    }
    return (
        <td className="border-l border-white/10 first:border-l-0 p-0 align-top">
            <div className="flex flex-col px-2 pt-1.5 pb-1">
                <div
                    className="text-[9px] uppercase tracking-wider font-bold text-slate-500 truncate"
                    title={column.name}
                    data-testid={`lead-cell-label-${rowId}-${column.id}`}
                >
                    {column.name}
                </div>
                <div className="flex items-center gap-1">
                    <input
                        type="text"
                        value={local}
                        onChange={(e) => { setLocal(e.target.value); beat(); }}
                        onFocus={beat}
                        onBlur={() => { if (local !== value) onSave(local); }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                            if (e.key === "Escape") { setLocal(value); e.target.blur(); }
                        }}
                        data-testid={`lead-cell-${rowId}-${column.id}`}
                        className="flex-1 min-w-0 bg-transparent py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:bg-[#1a3fd1]/5 focus:ring-1 focus:ring-[#1a3fd1]/40 rounded-sm"
                        placeholder="—"
                    />
                    {contactKind && (
                        <ContactLaunchIcons
                            value={value}
                            kind={contactKind}
                            testIdRoot={`lead-cell-${rowId}-${column.id}`}
                            compact
                        />
                    )}
                </div>
            </div>
        </td>
    );
}


/**
 * Mobile-only (md:hidden) collapsible panel that lets users rename or delete
 * the leads table columns. Exists because on mobile the sortable table header
 * (which owns the desktop "⋮" menu per column) is hidden — the sortable
 * header is the only place rename/delete live on desktop. On mobile we
 * surface the same actions in a simple stacked list.
 */
function MobileColumnManager({ columns, onRename, onDelete }) {
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState("");
    const [pendingDelete, setPendingDelete] = useState(null);

    const startEdit = (col) => {
        setEditingId(col.id);
        setDraft(col.name);
    };
    const commitEdit = () => {
        const c = columns.find((x) => x.id === editingId);
        if (c && draft.trim() && draft.trim() !== c.name) {
            onRename(editingId, draft.trim());
        }
        setEditingId(null);
        setDraft("");
    };

    return (
        <div className="md:hidden mb-3" data-testid="mobile-column-manager">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-column-manager-toggle"
                className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-white/[0.05] border border-white/15 hover:border-[#1a3fd1]/60 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-white transition-colors"
            >
                <span className="inline-flex items-center gap-1.5">
                    <Settings2 className="w-3.5 h-3.5 text-[#8fa4f0]" />
                    Edit columns · {columns.length}
                </span>
                <ChevronDown
                    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <ul
                    className="mt-2 rounded-sm bg-white/[0.05] border border-white/15 divide-y divide-white/10 overflow-hidden"
                    data-testid="mobile-column-manager-list"
                >
                    {columns.map((col) => (
                        <li key={col.id} className="p-2 flex items-center gap-2">
                            {editingId === col.id ? (
                                <>
                                    <input
                                        autoFocus
                                        type="text"
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") commitEdit();
                                            if (e.key === "Escape") {
                                                setEditingId(null);
                                                setDraft("");
                                            }
                                        }}
                                        data-testid={`mobile-column-rename-input-${col.id}`}
                                        className="flex-1 bg-[#141414] border border-[#1a3fd1]/60 rounded-sm px-2 py-1 text-sm text-white focus:outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={commitEdit}
                                        data-testid={`mobile-column-rename-save-${col.id}`}
                                        className="px-2 py-1 rounded-sm bg-[#1a3fd1]/20 text-[#8fa4f0] text-[11px] font-bold uppercase"
                                    >
                                        Save
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="flex-1 min-w-0 text-sm text-slate-200 truncate">
                                        {col.name}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => startEdit(col)}
                                        data-testid={`mobile-column-rename-${col.id}`}
                                        aria-label="Rename column"
                                        className="w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-[#8fa4f0] transition-colors"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPendingDelete(col)}
                                        data-testid={`mobile-column-delete-${col.id}`}
                                        aria-label="Delete column"
                                        disabled={columns.length <= 1}
                                        className="w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-rose-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && setPendingDelete(null)}
            >
                <AlertDialogContent className="bg-[#111] border-white/15">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete column "{pendingDelete?.name}"?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            All values entered in this column across every lead
                            will be permanently removed. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="bg-[#1a1a1a] border-white/15 text-slate-200">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                onDelete(pendingDelete.id);
                                setPendingDelete(null);
                            }}
                            data-testid="mobile-column-delete-confirm"
                            className="bg-rose-500 hover:bg-rose-600 text-white"
                        >
                            Delete column
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
